import { createPopper, type Instance } from '@popperjs/core';
import { type CSSProperties, useCallback, useEffect, useRef, useState } from 'react';

import type { IDropDownWrapper } from '../Dropdown.helpers';

export const DropdownWrapper = ({ children, dropDownRef }: IDropDownWrapper) => {
	const [visible, setVisible] = useState(false);
	const [dropdownStyles, setDropdownStyles] = useState<CSSProperties | undefined>(undefined);
	const [instance, setInstance] = useState<Instance | undefined>();

	const localButtonRef = useRef<HTMLButtonElement | null>(null);
	const buttonRef = dropDownRef ?? localButtonRef;
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const controlRef = useRef<HTMLDivElement | HTMLButtonElement | null>(null);

	useEffect(() => {
		let popperInstance: Instance | undefined;
		if (visible && controlRef.current && dropdownRef.current) {
			popperInstance = createPopper(controlRef.current, dropdownRef.current, {
				modifiers: [
					{
						name: 'offset',
						options: { offset: [0, 5] },
					},
					{
						name: 'preventOverflow',
						options: {
							boundary: 'viewport',
							padding: 10,
						},
					},
					{
						name: 'flip',
						options: {
							boundary: 'clippingParents',
							rootBoundary: 'document',
							fallbackPlacements: ['top-start', 'bottom-start', 'left-start'],
						},
					},
					{
						name: 'computeStyles',
						options: {
							adaptive: false,
							gpuAcceleration: false,
						},
					},
				],
			});
			setInstance(popperInstance);
		}

		const handleClickOutside = (e: MouseEvent) => {
			const isControlRef =
				controlRef.current &&
				!controlRef.current.contains(e.target as Node) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node);

			const isDropdownRef =
				buttonRef.current &&
				!buttonRef.current.contains(e.target as Node) &&
				dropdownRef.current &&
				!dropdownRef.current.contains(e.target as Node);

			if (isControlRef || isDropdownRef) {
				setVisible(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);

			if (popperInstance?.destroy !== undefined && typeof popperInstance.destroy === 'function') {
				popperInstance.destroy();
			}
		};
	}, [buttonRef, visible]);

	const handleVisibility = useCallback(() => {
		setVisible((v) => !v);
	}, []);

	useEffect(() => {
		if (controlRef.current) {
			const rect = controlRef.current.getBoundingClientRect();
			const styles: CSSProperties = {
				position: 'absolute',
				width: rect.width,
			};
			setDropdownStyles(styles);
		}
	}, [visible]);

	useEffect(() => {
		const handleResize = () => {
			if (controlRef.current && visible) {
				const rect = controlRef.current.getBoundingClientRect();
				setDropdownStyles({
					...dropdownStyles,
					width: rect.width,
				});
			}
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [visible, dropdownStyles]);

	return (
		<div className='dropdown_wrapper'>
			{children?.({
				buttonRef,
				handleVisibility,
				visible,
				dropdownRef,
				controlRef,
				dropdownStyles,
				tipPlacement: instance?.state.placement ?? '',
			})}
		</div>
	);
};
